import { Component, signal } from '@angular/core';

type LeadSubmissionStatus = 'idle' | 'submitting' | 'success' | 'error';

interface KarimLeadPayload {
  name: string;
  email: string;
  phonenumber: string;
  message: string;
  property_type: string;
  unit_type: string;
  budget: string;
  website: string;
  source: string;
  company: string;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  protected readonly submissionStatus = signal<LeadSubmissionStatus>('idle');
  protected readonly submissionMessage = signal('');

  private readonly leadEndpoint = 'https://api.karimexclusives.com/api/leads/';

  protected ensureVideoPlayback(video: HTMLVideoElement): void {
    video.defaultMuted = true;
    video.muted = true;

    if (video.paused) {
      void video.play().catch(() => {
        // Browsers may defer playback while the page is backgrounded.
      });
    }
  }

  protected openEnquiry(dialog: HTMLDialogElement): void {
    this.submissionStatus.set('idle');
    this.submissionMessage.set('');

    if (!dialog.open) {
      dialog.showModal();
    }
  }

  protected closeEnquiry(dialog: HTMLDialogElement): void {
    if (dialog.open) {
      dialog.close();
    }
  }

  protected closeFromBackdrop(event: MouseEvent, dialog: HTMLDialogElement): void {
    if (event.target === dialog) {
      this.closeEnquiry(dialog);
    }
  }

  protected submitEnquiry(form: HTMLFormElement, dialog: HTMLDialogElement): void {
    void this.submitEnquiryAsync(form, dialog);
  }

  private async submitEnquiryAsync(
    form: HTMLFormElement,
    dialog: HTMLDialogElement,
  ): Promise<void> {
    if (this.submissionStatus() === 'submitting') {
      return;
    }

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const formData = new FormData(form);
    const name = this.getFormValue(formData, 'fullName');
    const email = this.getFormValue(formData, 'email');
    const phoneNumber = this.getFormValue(formData, 'phone');
    const apartmentType = this.getFormValue(formData, 'apartmentType');
    const message = this.getFormValue(formData, 'message');
    const apartmentLabel =
      {
        'three-bedroom': '3 bedrooms',
        'four-bedroom': '4 bedrooms',
      }[apartmentType] ?? apartmentType;
    const payload: KarimLeadPayload = {
      name,
      email,
      phonenumber: phoneNumber,
      message: `Request: Sabah Vipingo enquiry\nApartment type: ${apartmentLabel}\nMessage: ${message}`,
      property_type: 'Apartment',
      unit_type: apartmentLabel,
      budget: '',
      website: window.location.hostname || 'sabahvipingo.karimexclusives.com',
      source: 'Sabah Vipingo Register Interest Form',
      company: 'Sabah Vipingo',
    };

    this.submissionStatus.set('submitting');
    this.submissionMessage.set('');

    try {
      const response = await fetch(this.leadEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.status !== 201) {
        throw new Error(`Lead API returned ${response.status}`);
      }

      this.submissionStatus.set('success');
      this.submissionMessage.set('Thank you. Our team will contact you shortly.');
      form.reset();
      window.setTimeout(() => this.closeEnquiry(dialog), 1200);
    } catch (error: unknown) {
      console.error(error);
      this.submissionStatus.set('error');
      this.submissionMessage.set('We could not send your details. Please try again.');
    }
  }

  private getFormValue(formData: FormData, key: string): string {
    const value = formData.get(key);
    return typeof value === 'string' ? value.trim() : '';
  }

  protected trackPointer(event: PointerEvent): void {
    if (event.pointerType === 'touch') {
      return;
    }

    const target = event.currentTarget as HTMLElement;
    const bounds = target.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
    const y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
    const cueSize = 72;
    const cue = target.querySelector<HTMLElement>('.discover-cue');

    target.style.setProperty('--media-x', `${(x * -5).toFixed(2)}px`);
    target.style.setProperty('--media-y', `${(y * -5).toFixed(2)}px`);
    target.style.setProperty('--media-hover-x', `${(x * -9).toFixed(2)}px`);
    target.style.setProperty('--media-hover-y', `${(y * -9).toFixed(2)}px`);
    cue?.style.setProperty(
      '--cue-x',
      `${Math.max(0, Math.min(bounds.width - cueSize, event.clientX - bounds.left - cueSize / 2)).toFixed(2)}px`,
    );
    cue?.style.setProperty(
      '--cue-y',
      `${Math.max(0, Math.min(bounds.height - cueSize, event.clientY - bounds.top - cueSize / 2)).toFixed(2)}px`,
    );
  }

  protected resetPointer(event: PointerEvent): void {
    const target = event.currentTarget as HTMLElement;
    target.style.setProperty('--media-x', '0px');
    target.style.setProperty('--media-y', '0px');
    target.style.setProperty('--media-hover-x', '0px');
    target.style.setProperty('--media-hover-y', '0px');
  }
}
